import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Input,
  Table,
  TableCaption,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tfoot,
  TableContainer,
  List,
  ListItem,
  Select,
  Button,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
//import toast from 'react-hot-toast';

const DailyComplementarySummary = () => {
  const [data, setData] = useState([]);
  const [wineData, setWineData] = useState([]);
  const [payment, setPayment] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(new Date());
  const [searchItem, setSearchItem] = useState("");
  const [unit, setUnit] = useState("");
  const [foodData, setFoodData] = useState([]);
  const [filteredFoodData, setFilteredFoodData] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [toggle, setToggle] = useState(false); // Track selected item index

  const customerId = localStorage.getItem("customerId");

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `https://api.rasoi.launchlysoftware.in/api/getComplementarySummary/${customerId}?startDate=${startDate}&endDate=${endDate}&searchItem=${searchItem}`,
      );
      setData(response.data.foodSales);
      setWineData(response.data.wineSales);
      setPayment(response.data.paymentSummary);
    } catch (error) {
      toast.error("Error fetching data");
    }
  };
  const handleToggle = () => {
    setToggle(!toggle);
  };

  const fetchFoodData = async () => {
    try {
      const response = await axios.get(
        `https://api.rasoi.launchlysoftware.in/api/getFoodAndWineData/${customerId}`,
      );
      setFoodData(response.data.items);
      setFilteredFoodData(response.data.items); // Initial filtered data to all items
    } catch (error) {
      toast.error("Error fetching food data");
    }
  };

  useEffect(() => {
    fetchData();
    fetchFoodData();
  }, [customerId, startDate, endDate, searchItem]);

  // Export to Excel function
  const consolidateDataForExcel = () => {
    const consolidatedData = [];
    const maxRows = Math.max(data.length, wineData.length, 1); // 1 for at least the payment row

    // Initialize total quantities and amounts
    let totalFoodQuantity = 0;
    let totalFoodAmount = 0;
    let totalwineQuantity = 0;
    let totalwineAmount = 0;

    for (let i = 0; i < maxRows; i++) {
      // Accumulate total quantities and amounts for food
      totalFoodQuantity += data[i]?.totalQuantity || 0;
      totalFoodAmount += data[i]?.totalAmount || 0;
      totalwineQuantity += wineData[i]?.totalQuantity || 0;
      totalwineAmount += wineData[i]?.totalAmount || 0;

      consolidatedData.push({
        Food_Item: data[i]?._id || "",
        Food_Rate: data[i]?.rate || "",
        Food_TotalQuantity: data[i]?.totalQuantity || "",
        Food_TotalAmount: data[i]?.totalAmount || "",
        Wine_Item: wineData[i]?._id || "",
        Wine_Rate: wineData[i]?.rate || "",
        Wine_TotalQuantity: wineData[i]?.totalQuantity || "",
        Wine_TotalAmount: wineData[i]?.totalAmount || "",
        Cash: i === 0 ? payment.cashTotal || "" : "", // Only show once
        Online: i === 0 ? payment.onlineTotal || "" : "",
        Card: i === 0 ? payment.cardTotal || "" : "",
        Discount: i === 0 ? payment.discountTotal || "" : "",
        Gst: i === 0 ? payment.gstTotal || "" : "",
        Due: i === 0 ? payment.dueTotal || "" : "",
        NetAmount:
          i === 0
            ? payment.cashTotal +
                payment.onlineTotal +
                payment.cardTotal +
                payment.gstTotal || ""
            : "",
      });
    }

    // Add a final row for totals
    consolidatedData.push({
      Food_Item: "Total",
      Food_Rate: "",
      Food_TotalQuantity: totalFoodQuantity,
      Food_TotalAmount: totalFoodAmount,
      Wine_Item: "",
      Wine_Rate: "",
      Wine_TotalQuantity: totalwineQuantity, // No total for wine quantity
      Wine_TotalAmount: totalwineAmount, // No total for wine amount
      Cash: "",
      Online: "",
      Card: "",
      Discount: "",
      Gst: "",
      Due: "",
      NetAmount: "",
    });

    return consolidatedData;
  };

  const exportToExcel = () => {
    const dataToExport = consolidateDataForExcel();
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Sales Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${startDate}-${endDate}-ComplementarySalesSummary.xlsx`);
  };
  const exportToPDF = () => {
    const dataToExport = consolidateDataForExcel();

    // Initialize jsPDF
    const pdf = new jsPDF();

    // Define the columns for the table
    const columns = [
      { header: "Food Item", dataKey: "Food_Item" },
      { header: "Food Rate", dataKey: "Food_Rate" },
      { header: "Food Total Quantity", dataKey: "Food_TotalQuantity" },
      { header: "Food Total Amount", dataKey: "Food_TotalAmount" },
      { header: "Wine Item", dataKey: "Wine_Item" },
      { header: "Wine Rate", dataKey: "Wine_Rate" },
      { header: "Wine Total Quantity", dataKey: "Wine_TotalQuantity" },
      { header: "Wine Total Amount", dataKey: "Wine_TotalAmount" },
      { header: "Cash", dataKey: "Cash" },
      { header: "Online", dataKey: "Online" },
      { header: "Card", dataKey: "Card" },
      { header: "Discount", dataKey: "Discount" },
      { header: "Gst", dataKey: "Gst" },
      { header: "Due", dataKey: "Due" },
      { header: "Net Amount", dataKey: "NetAmount" },
    ];

    // Add title to PDF
    pdf.text("Daily Sales Summary", 14, 20);

    // Add autoTable to render the data in a table format
    pdf.autoTable({
      startY: 30,
      head: [columns.map((col) => col.header)],
      body: dataToExport.map((row) =>
        columns.map((col) => row[col.dataKey] || ""),
      ),
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [0, 57, 107],
        textColor: [255, 255, 255],
      },
    });

    // Save the PDF
    pdf.save(`${startDate}-${endDate}ComplementarySalesSummary.pdf`);
  };

  // Filter foodData based on searchItem input
  useEffect(() => {
    if (searchItem) {
      const filtered = foodData.filter((item) =>
        item.name.toLowerCase().includes(searchItem.toLowerCase()),
      );
      setFilteredFoodData(filtered);
    } else {
      setFilteredFoodData(foodData);
    }
    setHighlightedIndex(-1); // Reset the highlight when search input changes
  }, [searchItem, foodData]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prevIndex) =>
        prevIndex < filteredFoodData.length - 1 ? prevIndex + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : filteredFoodData.length - 1,
      );
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      // Set the search input to the selected item
      setSearchItem(filteredFoodData[highlightedIndex].name);
      setFilteredFoodData([]); // Clear suggestions after selection
    }
  };

  return (
    <Box p={6} bg="gray.50" minH="100vh">
      <Box
        display="flex"
        flexWrap="wrap"
        gap={4}
        mb={6}
        p={4}
        bg="white"
        borderRadius="lg"
        boxShadow="md"
      >
        <Input
          flex="1"
          minW="180px"
          type="date"
          name="startDate"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          bg="white"
        />
        <Input
          flex="1"
          minW="180px"
          type="date"
          name="endDate"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          bg="white"
        />
        <Button colorScheme="blue" onClick={handleToggle}>
          {toggle ? "Hide List" : "Show List"}
        </Button>

        {toggle && (
          <Box minW="200px" position="relative">
            <Input
              placeholder="Search Item"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target.value)}
              onKeyDown={handleKeyDown}
              bg="white"
            />
            <List
              position="absolute"
              bg="white"
              zIndex="10"
              width="100%"
              maxHeight="200px"
              overflowY="auto"
              boxShadow="lg"
              borderRadius="md"
              mt={1}
            >
              {filteredFoodData.map((item, index) => (
                <ListItem
                  key={index}
                  bg={index === highlightedIndex ? "gray.200" : "white"}
                  padding="8px"
                  cursor="pointer"
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                  onClick={() => setSearchItem(item.name)}
                  _hover={{ bg: "gray.100" }}
                >
                  {item.name}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <Button colorScheme="green" onClick={exportToExcel}>
          Export Excel
        </Button>
        <Button colorScheme="red" onClick={exportToPDF}>
          Export PDF
        </Button>
      </Box>

      <Box width="full" bg="white" borderRadius="lg" boxShadow="md" p={4}>
        <Box
          width="full"
          maxH="calc(100vh - 350px)"
          overflowY="auto"
          overflowX="auto"
        >
          <Table size="sm" variant="simple">
            <TableCaption
              placement="top"
              fontSize="lg"
              fontWeight="bold"
              mb={4}
            >
              Complementary Sales Summary
            </TableCaption>
            <Thead position="sticky" top={0} bg="green.500" zIndex={10}>
              <Tr>
                <Th color="white">Food Items</Th>
                <Th color="white">Wine Items</Th>
                <Th color="white">Payment Details</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td verticalAlign="top" p={3}>
                  {/* Food Sales Table */}
                  <Box
                    maxH="500px"
                    overflowY="auto"
                    bg="orange.50"
                    borderRadius="md"
                    p={2}
                  >
                    <Table size="sm" variant="simple">
                      <TableCaption placement="top" fontWeight="semibold">
                        Food Sales Summary
                      </TableCaption>
                      <Thead
                        position="sticky"
                        top={0}
                        bg="orange.400"
                        zIndex={1}
                      >
                        <Tr>
                          <Th color="white">Name</Th>
                          <Th color="white" isNumeric>
                            Rate
                          </Th>
                          <Th color="white" isNumeric>
                            Qty
                          </Th>
                          <Th color="white" isNumeric>
                            Amount
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.map((item, index) => (
                          <Tr key={index}>
                            <Td>{item._id}</Td>
                            <Td isNumeric>₹{item.rate}</Td>
                            <Td isNumeric>{item.totalQuantity}</Td>
                            <Td isNumeric fontWeight="semibold">
                              ₹{item.totalAmount}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                      <Tfoot bg="orange.100">
                        <Tr>
                          <Td colSpan={2} textAlign="right" fontWeight="bold">
                            Total:
                          </Td>
                          <Td isNumeric fontWeight="bold">
                            {data.reduce(
                              (total, item) => total + item.totalQuantity,
                              0,
                            )}
                          </Td>
                          <Td isNumeric fontWeight="bold">
                            ₹
                            {data.reduce(
                              (total, item) => total + item.totalAmount,
                              0,
                            )}
                          </Td>
                        </Tr>
                      </Tfoot>
                    </Table>
                  </Box>
                </Td>
                <Td verticalAlign="top" p={3}>
                  {/* Wine Sales Table */}
                  <Box
                    maxH="500px"
                    overflowY="auto"
                    bg="blue.50"
                    borderRadius="md"
                    p={2}
                  >
                    <Table size="sm" variant="simple">
                      <TableCaption placement="top" fontWeight="semibold">
                        Wine Sales Summary
                      </TableCaption>
                      <Thead position="sticky" top={0} bg="blue.400" zIndex={1}>
                        <Tr>
                          <Th color="white">Name</Th>
                          <Th color="white" isNumeric>
                            Rate
                          </Th>
                          <Th color="white" isNumeric>
                            Qty
                          </Th>
                          <Th color="white" isNumeric>
                            Amount
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {wineData.map((item, index) => (
                          <Tr key={index}>
                            <Td>{item._id}</Td>
                            <Td isNumeric>₹{item.rate}</Td>
                            <Td isNumeric>{item.totalQuantity}</Td>
                            <Td isNumeric fontWeight="semibold">
                              ₹{item.totalAmount}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                      <Tfoot bg="blue.100">
                        <Tr>
                          <Td colSpan={2} textAlign="right" fontWeight="bold">
                            Total:
                          </Td>
                          <Td isNumeric fontWeight="bold">
                            {wineData.reduce(
                              (total, item) => total + item.totalQuantity,
                              0,
                            )}
                          </Td>
                          <Td isNumeric fontWeight="bold">
                            ₹
                            {wineData.reduce(
                              (total, item) => total + item.totalAmount,
                              0,
                            )}
                          </Td>
                        </Tr>
                      </Tfoot>
                    </Table>
                  </Box>
                </Td>
                <Td verticalAlign="top" p={3}>
                  {/* Payment Summary Table */}
                  <Box bg="yellow.50" borderRadius="md" p={2}>
                    <Table size="sm" variant="simple">
                      <TableCaption placement="top" fontWeight="semibold">
                        Payment Summary
                      </TableCaption>
                      <Thead bg="yellow.400">
                        <Tr>
                          <Th color="white">Type</Th>
                          <Th color="white" isNumeric>
                            Amount
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>Cash</Td>
                          <Td isNumeric>₹{payment.cashTotal}</Td>
                        </Tr>
                        <Tr>
                          <Td>Online</Td>
                          <Td isNumeric>₹{payment.onlineTotal}</Td>
                        </Tr>
                        <Tr>
                          <Td>Card</Td>
                          <Td isNumeric>₹{payment.cardTotal}</Td>
                        </Tr>
                        <Tr>
                          <Td>Discount</Td>
                          <Td isNumeric>₹{payment.discountTotal}</Td>
                        </Tr>
                        <Tr>
                          <Td>GST</Td>
                          <Td isNumeric>₹{payment.gstTotal}</Td>
                        </Tr>
                        <Tr>
                          <Td>Due</Td>
                          <Td isNumeric>₹{payment.dueTotal}</Td>
                        </Tr>
                        <Tr bg="yellow.100">
                          <Td fontWeight="bold">Net Amount</Td>
                          <Td isNumeric fontWeight="bold" fontSize="lg">
                            ₹
                            {payment.cashTotal +
                              payment.onlineTotal +
                              payment.cardTotal}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default DailyComplementarySummary;
